import { GET, PATCH, POST } from "../config/axios.config";
import { normalizeCoupons, normalizeOrder, normalizeOrders } from "./normalizers";

/* ── Orders ───────────────────────────────────────────────────────────── */

/**
 * v2 prices the order itself: the client sends only what was chosen
 * (address, items, coupon code, payment intent) and the server computes
 * subtotal, delivery fee and discount from its own catalogue.
 */
export const createOrder = async ({
  addressId,
  items,
  couponCode,
  notes,
  paymentMethod = "cod",
}) => {
  const res = await POST("/orders", {
    addressId,
    items: items.map((item) => ({
      foodId: item.foodId,
      quantity: Number(item.quantity),
    })),
    ...(couponCode ? { couponCode } : {}),
    ...(notes ? { notes } : {}),
    paymentMethod,
  });
  return { ...res, data: normalizeOrder(res?.data?.order) };
};

export const getMyOrders = async ({ page = 1, limit = 20, status } = {}) => {
  const res = await GET("/orders", {
    params: { page, limit, ...(status ? { status } : {}) },
  });
  const pagination = res?.pagination || { total: 0, page, limit, totalPages: 1 };
  return {
    ...res,
    data: normalizeOrders(res?.data),
    pagination,
    hasMore: pagination.page < pagination.totalPages,
    nextPage: pagination.page + 1,
  };
};

export const getOrderById = async (orderId) => {
  const res = await GET(`/orders/${orderId}`);
  return { ...res, data: normalizeOrder(res?.data?.order) };
};

export const cancelOrder = async (orderId, reason) => {
  const res = await PATCH(`/orders/${orderId}/cancel`, {
    ...(reason ? { reason } : {}),
  });
  return { ...res, data: normalizeOrder(res?.data?.order) };
};

/**
 * The OwnFood rider for a platform-delivered order: name, phone (only while
 * the job is live) and last position. `null` for a kitchen-delivered order or
 * before a rider has been requested.
 */
export const getOrderTracking = async (orderId) => {
  const res = await GET(`/orders/${orderId}/tracking`);
  return res?.data?.tracking || null;
};

/** 1–5 stars for the rider, once the order is delivered. */
export const rateOrderRider = (orderId, rating) =>
  POST(`/orders/${orderId}/rider-rating`, { rating: Number(rating) });

/** Server-side delivery quote for a kitchen/address pair, used before checkout. */
export const getDeliveryQuote = async ({ chefId, addressId }) => {
  const res = await POST("/orders/delivery-quote", { chefId, addressId });
  return res?.data?.quote || null;
};

/* ── Coupons ──────────────────────────────────────────────────────────── */

export const fetchKitchenCoupons = async (chefId) => {
  const res = await GET("/coupons", { params: chefId ? { chefId } : {} });
  return normalizeCoupons(res?.data?.coupons);
};

export const validateCoupon = async ({ code, chefId, subtotal }) => {
  const res = await POST("/coupons/validate", {
    code,
    chefId,
    subtotal: Number(subtotal),
  });
  return res;
};

/* ── Payments ─────────────────────────────────────────────────────────── */

/**
 * Both gateways return `{ payment, redirectUrl }` for web clients — the
 * callback URLs are configured server-side, so nothing is passed in.
 */
export const initiateEpsPayment = (orderId) =>
  POST("/payments/eps/initiate", { orderId, client: "web" });

export const initiateStripePayment = (orderId) =>
  POST("/payments/stripe/initiate", { orderId, client: "web" });

/** Confirms a returning Stripe checkout session against the API. */
export const verifyStripePayment = (transactionId) =>
  GET(`/payments/stripe/verify/${encodeURIComponent(transactionId)}`);

/** Asks the API to confirm a returning EPS payment with EPS itself. */
export const verifyEpsPayment = (transactionId) =>
  GET(`/payments/eps/verify/${encodeURIComponent(transactionId)}`);
